/**
 * Backend API service for the Form Designer (uses the browser `fetch` API, no
 * extra dependency).
 *
 * The designer runs on its own origin (localhost:3001), so it keeps its own JWT
 * (localStorage keys below) rather than sharing the main app's token. Endpoints
 * map to the versioned-form API added on the backend:
 *   POST /api/main/forms/                          create form + v1
 *   GET  /api/main/forms/{id}/versions/            version history
 *   GET  /api/main/forms/{id}/versions/{v}         one version (schema)
 *   POST /api/main/forms/{id}/versions/            add draft (optional publish)
 *   POST /api/main/forms/{id}/versions/{v}/publish publish a version
 */
export const DOMAIN = window.location.href.includes("localhost")
  ? "http://localhost:8000"
  : window.location.origin;

const ACCESS_KEY = "designer_access";
const REFRESH_KEY = "designer_refresh";

export const getAccessToken = () => localStorage.getItem(ACCESS_KEY) || "";
export const isAuthenticated = () => !!getAccessToken();
export const logout = () => {
  localStorage.removeItem(ACCESS_KEY);
  localStorage.removeItem(REFRESH_KEY);
};

async function request(path, { method = "GET", body, auth = true } = {}) {
  const headers = { "Content-Type": "application/json" };
  if (auth) headers.Authorization = `Bearer ${getAccessToken()}`;
  const res = await fetch(`${DOMAIN}${path}`, {
    method,
    headers,
    body: body === undefined ? undefined : JSON.stringify(body),
  });
  const text = await res.text();
  const data = text ? JSON.parse(text) : null;
  if (!res.ok) {
    const detail =
      (data && (data.detail || JSON.stringify(data))) || res.statusText;
    const err = new Error(detail);
    err.status = res.status;
    throw err;
  }
  return data;
}

export const login = async (email, password) => {
  const data = await request("/api/accounts/login/", {
    method: "POST",
    body: { email, password },
    auth: false,
  });
  localStorage.setItem(ACCESS_KEY, data.access);
  if (data.refresh) localStorage.setItem(REFRESH_KEY, data.refresh);
  return data;
};

// --- Templates / types (for the metadata pickers) ------------------------- //
// `type=admin` scopes to the template types the current user can manage. The
// admin endpoint nests templates under `filtered_templates`; normalize to a
// stable `{ id, name, templates: [{id, name, ...}] }` shape for the UI.
export const getTemplateTypes = async () => {
  const data = await request("/api/main/template-types/?type=admin");
  return (data || []).map((tt) => ({
    id: tt.id,
    name: tt.name,
    templates: tt.filtered_templates || tt.templates || [],
  }));
};

// --- Forms / versions ----------------------------------------------------- //
export const createForm = (payload) =>
  request("/api/main/forms/", { method: "POST", body: payload });

export const listForms = async (templateId) => {
  const data = await request("/api/main/forms/");
  return templateId ? data.filter((f) => f.template === templateId) : data;
};

export const listVersions = (formId) =>
  request(`/api/main/forms/${formId}/versions/`);

// Trailing slashes are required: DRF's router registers these routes with a
// trailing slash and Django's APPEND_SLASH cannot redirect a POST body.
export const getVersion = (formId, version) =>
  request(`/api/main/forms/${formId}/versions/${version}/`);

export const createDraft = (formId, payload) =>
  request(`/api/main/forms/${formId}/versions/`, {
    method: "POST",
    body: payload,
  });

export const publishVersion = (formId, version) =>
  request(`/api/main/forms/${formId}/versions/${version}/publish/`, {
    method: "POST",
    body: {},
  });
